import { effect, untracked } from "@preact/signals";
import * as preact from "preact";
import * as _ from "underscore";

import * as deviceDetect from "deviceDetect";
import { shownAgain } from "pages/PreactPage";
import * as languageMap from "shared/languageMap";
import BookCollectionStore from "stores/BookCollectionStore";
import SearchQueryStore from "stores/SearchQueryStore";
import stores from "stores/stores";
import { PrivateLibraryList } from "views/PrivateLibraryListView";
import PrivateLibraryTagSelectorView from "views/PrivateLibraryTagSelectorView";
import SearchView from "views/SearchView";
import ShelfEditView from "views/ShelfEditView";
import SkeletonView from "views/SkeletonView";
import * as walkthrough from "walkthrough";

interface AppRouterLike {
  navigate(path: string, options?: { trigger?: boolean }): void;
}

interface PrivateLibraryViewProps {
  /** Not while kept, hidden, as another page shows */
  active?: boolean;
  /** How many times its tab's been reselected */
  reselections?: number;
  appRouter: AppRouterLike;
  shelfID?: string;
  tag?: string;
  renderedCallback: () => void;
  /** Ending the list's controls, or on their own with no texts */
  addButtons?: preact.ComponentChildren;
}

/** My Texts' URL, at the shelf or tag last shown */
export const myTextsPath = () => {
  const language = stores.user.currentLearningLanguage.value;
  const shelfID = stores.shelves.selectedShelfID.value;
  return shelfID
    ? `/${language}/myTexts/shelf/${shelfID}`
    : `/${language}/myTexts/${stores.bookTags.activeTag.value ?? "queue"}`;
};

interface PrivateLibraryState {
  initialized: boolean;
  initializationFailed: boolean;
}

export class PrivateLibrary extends preact.Component<
  PrivateLibraryViewProps,
  PrivateLibraryState
> {
  private readonly books = new BookCollectionStore();
  private readonly searchQuery = new SearchQueryStore();
  /** The tag or shelf shown before a search (of all texts), to go back to */
  private browsedBeforeSearch?: {
    tag: string | null;
    shelfID: string | null;
  };
  private bookTagsSyncVersion = 0;
  private disposeActiveTagEffect?: () => void;
  private disposeReadingProgress?: () => void;
  private disposeBooksEffect?: () => void;
  private disposeDeletedAllEffect?: () => void;
  private disposeRestoredBookEffect?: () => void;
  private disposeSearchQueryChange?: () => void;
  private disposeShelfIDEffect?: () => void;
  private fetchingTimeout?: ReturnType<typeof setTimeout>;
  private previousBookTags?: Map<string, string[]>;
  private suppressFetch = false;
  private unmounted = false;

  state: PrivateLibraryState = {
    initialized: false,
    initializationFailed: false,
  };

  componentDidMount() {
    this.disposeBooksEffect = effect(() => {
      const currentBookTags = new Map<string, string[]>();
      for (const book of this.books.books.value) {
        const tags = (book.book.value.userBook?.tags ?? []) as string[];
        currentBookTags.set(book.id, [...tags]);
      }

      const version = ++this.bookTagsSyncVersion;
      Promise.resolve().then(() => {
        if (this.bookTagsSyncVersion === version) {
          this.syncBookTagCounts(currentBookTags);
          this.forceUpdate();
        }
      });
    });

    this.disposeRestoredBookEffect = effect(() => {
      const restoredBook = this.books.restoredBook.value;
      if (restoredBook) {
        Promise.resolve().then(() => {
          untracked(() => stores.shelves.addNewBook(restoredBook.bookID));
        });
      }
    });

    this.disposeDeletedAllEffect = effect(() => {
      const deletedAll = this.books.deletedAll.value;
      if (deletedAll) {
        Promise.resolve().then(() => {
          untracked(() => stores.bookTags.setTagCount(deletedAll.tag, 0));
        });
      }
    });

    this.disposeReadingProgress = this.books.followReadingProgress();

    void this.initialize();
  }

  componentDidUpdate(previousProps: PrivateLibraryViewProps) {
    // Back from another page, where texts may have been uploaded or moved,
    // or its tab reselected
    if (this.state.initialized && shownAgain(this.props, previousProps)) {
      void stores.bookTags.fetch({
        language: stores.user.currentLearningLanguage.value,
      });
      this.fetchBooks();
    }
  }

  componentWillUnmount() {
    this.unmounted = true;
    this.disposeReadingProgress?.();
    this.bookTagsSyncVersion++;
    clearTimeout(this.fetchingTimeout);
    this.disposeActiveTagEffect?.();
    this.disposeBooksEffect?.();
    this.disposeDeletedAllEffect?.();
    this.disposeRestoredBookEffect?.();
    this.disposeSearchQueryChange?.();
    this.disposeShelfIDEffect?.();
  }

  async initialize() {
    stores.shelves.reset();
    // On fetch failure show a retryable error instead of the throbber.
    // Continuing with empty shelves would silently navigate the user off
    // their shelf URL below. (Terminal failures were already notified
    // globally by readlangClient.)
    let fetchFailed = false;
    const flagFetchFailure = () => {
      fetchFailed = true;
    };
    await Promise.all([
      stores.bookTags
        .fetch({ language: stores.user.currentLearningLanguage.value })
        .catch(flagFetchFailure),
      stores.shelves
        .fetch({ language: stores.user.currentLearningLanguage.value })
        .catch(flagFetchFailure),
    ]);
    if (this.unmounted) return;
    if (fetchFailed) {
      this.setState({ initializationFailed: true });
      return;
    }

    const shelfID = this.props.shelfID || stores.shelves.selectedShelfID.value;
    if (shelfID) {
      if (stores.shelves.get(shelfID)) {
        stores.shelves.selectedShelfID.value = shelfID;
      } else {
        stores.shelves.selectedShelfID.value = null;
        this.props.appRouter.navigate("/myTexts/queue", { trigger: true });
        return;
      }
    } else if (this.props.tag) {
      stores.bookTags.activeTag.value = this.props.tag;
    } else if (!stores.bookTags.activeTag.value) {
      stores.bookTags.activeTag.value = "queue";
    }

    this.resetBookTagSnapshot();
    try {
      await this.books.fetch({
        language: stores.user.currentLearningLanguage.value,
        shelfID: stores.shelves.selectedShelfID.value,
        tag: !stores.shelves.selectedShelfID.value
          ? stores.bookTags.activeTag.value
          : undefined,
      });
    } catch {
      if (!this.unmounted) {
        this.setState({ initializationFailed: true });
      }
      return;
    }
    if (this.unmounted) return;

    let skipFirstActiveTag = true;
    this.disposeActiveTagEffect = effect(() => {
      void stores.bookTags.activeTag.value;
      if (skipFirstActiveTag) {
        skipFirstActiveTag = false;
        return;
      }
      this.fetchBooks();
    });

    let skipFirstShelfID = true;
    this.disposeShelfIDEffect = effect(() => {
      void stores.shelves.selectedShelfID.value;
      if (skipFirstShelfID) {
        skipFirstShelfID = false;
        return;
      }
      this.fetchBooks();
    });

    this.disposeSearchQueryChange = this.searchQuery.onQueryChange(
      (query, previousQuery) => {
        if (query && !previousQuery) {
          this.browsedBeforeSearch = {
            tag: stores.bookTags.activeTag.value,
            shelfID: stores.shelves.selectedShelfID.value,
          };
        } else if (!query && this.browsedBeforeSearch) {
          // The search cleared (a tag or shelf picked instead replaces this)
          this.suppressFetch = true;
          stores.bookTags.activeTag.value = this.browsedBeforeSearch.tag;
          stores.shelves.selectedShelfID.value =
            this.browsedBeforeSearch.shelfID;
          this.suppressFetch = false;
          this.browsedBeforeSearch = undefined;
        }
        this.fetchBooks();
      }
    );

    this.setState({ initialized: true }, () => {
      this.syncRoute();
      this.props.renderedCallback();
    });
  }

  fetchBooks() {
    if (this.suppressFetch) return;
    clearTimeout(this.fetchingTimeout);
    this.fetchingTimeout = setTimeout(() => {
      void this.fetchBooksNow();
    }, 1);
  }

  async fetchBooksNow() {
    const shelfID = stores.shelves.selectedShelfID.value;
    this.books.selectionStore.clearSelection();

    const fetchQuery: {
      language: string;
      searchQuery?: string;
      shelfID?: string;
      tag?: string | null;
    } = {
      language: stores.user.currentLearningLanguage.value,
    };

    if (this.searchQuery.query.value.length > 0) {
      // Searches every text but the deleted ones
      fetchQuery.searchQuery = this.searchQuery.query.value;
      fetchQuery.tag = "all";

      this.suppressFetch = true;
      stores.bookTags.activeTag.value = "all";
      stores.shelves.selectedShelfID.value = null;
      this.suppressFetch = false;
    } else if (shelfID) {
      fetchQuery.shelfID = shelfID;
    } else {
      fetchQuery.tag = stores.bookTags.activeTag.value;
    }

    this.resetBookTagSnapshot();
    await this.books.fetch(fetchQuery);
    if (this.unmounted) return;
    this.syncRoute();
    this.forceUpdate();
    this.props.renderedCallback();
  }

  resetBookTagSnapshot() {
    // Fetches replace the visible result set when changing filters/searches.
    // Count deltas should only compare local tag edits within one result set.
    this.previousBookTags = undefined;
  }

  syncBookTagCounts(currentBookTags: Map<string, string[]>) {
    const previousBookTags = this.previousBookTags;
    if (previousBookTags) {
      untracked(() => {
        for (const [bookID, tags] of currentBookTags.entries()) {
          const previous = previousBookTags.get(bookID);
          if (previous) {
            const toAdd = _.difference(tags, previous);
            const toRemove = _.difference(previous, tags);

            stores.bookTags.addTags(toAdd, previous);
            stores.bookTags.removeTags(toRemove, tags);
          }
        }
        for (const [bookID, tags] of previousBookTags.entries()) {
          if (!currentBookTags.has(bookID)) {
            if (tags.length === 0) {
              stores.bookTags.removeDeletedBook();
            } else {
              console.log(
                "weird - removing book which still has tags!?",
                bookID
              );
            }
          }
        }
      });
    }
    this.previousBookTags = currentBookTags;
  }

  syncRoute() {
    this.props.appRouter.navigate(myTextsPath());
  }

  completionCallout() {
    if (stores.bookTags.activeTag.value !== "completed") return null;

    const booksCompleted = this.books.length;
    const useCharacters = languageMap.usesCharacterCount(
      stores.user.currentLearningLanguage.value
    );
    const totalUnits = this.books.books.value.reduce(
      (memo, book) =>
        memo +
        ((useCharacters
          ? book.book.value.metrics?.characters
          : book.book.value.metrics?.words) ?? 0),
      0
    );
    const unitLabel = useCharacters ? "characters" : "words";

    if (booksCompleted === 0) {
      return (
        <div class="callout">
          You haven't completed any texts yet. Continue reading from your queue
          or one of the public texts.
        </div>
      );
    }
    if (booksCompleted === 1) {
      return (
        <div class="callout">
          You've completed your first text containing a total of {totalUnits}{" "}
          {unitLabel}, nice work! Don't stop there though, get cracking on your
          second one.
        </div>
      );
    }
    if (booksCompleted < 5) {
      return (
        <div class="callout">
          You've completed {booksCompleted} texts containing a total of{" "}
          {totalUnits} {unitLabel}, keep up the good work.
        </div>
      );
    }
    return (
      <div class="callout">
        You've completed {booksCompleted} texts containing a total of{" "}
        {totalUnits} {unitLabel}, great work! Keep it up.
      </div>
    );
  }

  noBooksMessage() {
    return (
      <div class="noBooksMessage">
        <p>
          {deviceDetect.isIOSApp() ? "Tap" : "Click"}{" "}
          <strong>
            <i class="icon-circle-arrow-up"></i> Upload
          </strong>{" "}
          to add your own texts.
        </p>
        <img src="/content/uploadArrow.svg" width="90" height="112" alt="" />
      </div>
    );
  }

  render() {
    if (this.state.initializationFailed) {
      return (
        <p style="text-align:center;margin-top:10px">
          Couldn't load your texts.{" "}
          <a
            href="#"
            onClick={(event) => {
              event.preventDefault();
              this.setState({ initializationFailed: false });
              void this.initialize();
            }}
          >
            Try again
          </a>
        </p>
      );
    }

    if (!this.state.initialized) {
      return <SkeletonView kind="rows" count={8} />;
    }

    const shelfID = stores.shelves.selectedShelfID.value;
    const activeTag = shelfID ? undefined : stores.bookTags.activeTag.value;
    const showShelfEditView = Boolean(shelfID) && shelfID !== "unshelved";
    let noBooks = false;

    if (!walkthrough.hasAchievement("privateTexts")) {
      if (this.books.length === 0) {
        noBooks = true;
      } else {
        walkthrough.setAchievement("privateTexts");
      }
    }

    return (
      <div>
        {noBooks ? this.props.addButtons : null}
        {!noBooks ? this.completionCallout() : this.noBooksMessage()}

        {showShelfEditView ? (
          <div data-subview="shelfEditView">
            <ShelfEditView
              appRouter={this.props.appRouter}
              books={this.books}
              shelfID={shelfID ?? undefined}
            />
          </div>
        ) : null}

        <div data-subview="libraryListView">
          {!noBooks ? (
            <PrivateLibraryList
              key={`${shelfID ?? ""}-${activeTag ?? ""}`}
              books={this.books}
              shelfID={shelfID}
              tag={activeTag ?? undefined}
              addButtons={this.props.addButtons}
              searchControls={
                <div class="libraryToolbar">
                  <span
                    class="browse"
                    data-subview="privateLibraryTagSelectorView"
                  >
                    <PrivateLibraryTagSelectorView
                      searchQuery={this.searchQuery}
                    />
                  </span>
                  <div class="search" data-subview="searchView">
                    <SearchView model={this.searchQuery} />
                  </div>
                </div>
              }
            />
          ) : null}
        </div>
      </div>
    );
  }
}
